import {
  type CheckoutInitTokenTransferResponse,
  type FunAddress,
  initializeCheckoutTokenTransferAddress,
} from '@funkit/api-base'
import {
  type CheckoutTokenTransferConfig,
  getCheckoutTokenTransferParams,
} from '@funkit/connect-core'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'

import { useFunkitIdentity } from '../../identity/IdentityContext'
import { logger } from '../../logging/logger'
import { useDepositRecipient } from './useDepositRecipient'
import { useUDAParams } from './useUDAParams'
import type {
  DepositClientMetadata,
  DepositTargetConfig,
} from '~/flow/depositFlowConfig'

export interface UseTransferInitParams {
  /** Destination chain/asset to derive the deposit address for. */
  checkoutConfig: DepositTargetConfig | undefined
  /**
   * Final recipient, outranking the config's `customRecipient`. Both default
   * to the identity's wallet address.
   */
  recipientAddr?: FunAddress
  /** Dynamic-routing id used to resolve UDA params. */
  dynamicRoutingId?: string
  /**
   * Active flow's checkoutId, threaded to `/eoa` so the backend can scope the
   * generated UDA to this checkout attempt. Caller must keep this stable for
   * the life of the flow (see `FunkitFlowProvider`).
   */
  checkoutId?: string
  /**
   * Host-supplied correlation ids (e.g. `externalUserId`/`clientTransactionId`),
   * sent to `/eoa`. Must contain only stable JSON-serializable properties.
   */
  clientMetadata?: DepositClientMetadata
  /** Gate the network call (e.g. only when the screen is visible). */
  enabled?: boolean
}

export interface UseTransferInitResult {
  transferInit: CheckoutInitTokenTransferResponse | undefined
  recipientAddr: FunAddress
  isLoading: boolean
  isFetching: boolean
  /** The `/eoa` call that mints the deposit address failed. */
  error: Error | undefined
  /** Replays that call, for a caller that puts a retry on screen. */
  retry: () => void
}

/**
 * Thin RN reimplementation of `@funkit/connect`'s `useCheckoutTransferInit`:
 * builds the (customer-agnostic) transfer params via the core helper and
 * creates the deposit-address EOA through `@funkit/api-base`. Identity comes
 * from {@link useFunkitIdentity}; web's Polymarket target override and geo /
 * post-action gating are dropped. Lighter's main-account override is kept —
 * see {@link useLighterDepositRecipientOverride}.
 */
export function useTransferInit({
  checkoutConfig,
  recipientAddr,
  dynamicRoutingId,
  checkoutId,
  clientMetadata,
  enabled = true,
}: UseTransferInitParams): UseTransferInitResult {
  const { apiKey, userId } = useFunkitIdentity()
  const udaParams = useUDAParams(dynamicRoutingId)
  const {
    recipient: resolvedRecipient,
    isLighterWalletFallback,
    isLighterDeposit,
    lighterRecipientOverride,
    isLookupFetching,
    lookupStatus,
  } = useDepositRecipient({
    checkoutConfig,
    recipientAddr,
    udaTargetChain: udaParams?.targetChain,
  })

  // Money-path decision log — same key/payload as web's useCheckoutTransferInit.
  useEffect(() => {
    if (isLighterDeposit && lighterRecipientOverride) {
      logger.info('checkout:recipient:lighterMainAccount', {
        mainAccountIndex: lighterRecipientOverride,
        targetChain: udaParams?.targetChain,
      })
    }
  }, [isLighterDeposit, lighterRecipientOverride, udaParams?.targetChain])

  // The hold is user-visible (no deposit address) — surface it once the lookup
  // settles without an index instead of staying silent.
  useEffect(() => {
    if (isLighterWalletFallback && !isLookupFetching) {
      logger.warn('checkout:recipient:lighterMainAccountUnresolved', {
        targetChain: udaParams?.targetChain,
        lookupStatus,
      })
    }
  }, [
    isLighterWalletFallback,
    isLookupFetching,
    lookupStatus,
    udaParams?.targetChain,
  ])

  // `undefined` when required params are missing — also disables the query.
  // connect-core types the target as an EVM address; `/eoa` takes a Solana
  // mint in the same field, so the RN-widened config is passed through as-is.
  const params = getCheckoutTokenTransferParams({
    checkoutConfig: checkoutConfig as CheckoutTokenTransferConfig | undefined,
    userId,
    apiKey,
    recipientAddr: resolvedRecipient,
    udaParams,
  })

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [
      'initializeCheckoutTokenTransferAddress',
      params,
      checkoutId,
      clientMetadata,
    ] as const,
    queryFn: ({ queryKey: [, queryParams] }) =>
      queryParams
        ? initializeCheckoutTokenTransferAddress({
            ...queryParams,
            logger,
            checkoutId,
            clientMetadata: clientMetadata ?? {},
          })
        : undefined,
    enabled: enabled && !!params && !isLighterWalletFallback,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const retry = useCallback(() => void refetch(), [refetch])

  return {
    transferInit: isFetching ? undefined : data,
    recipientAddr: resolvedRecipient,
    isLoading,
    isFetching,
    error: error ?? undefined,
    retry,
  }
}
